// =========================================
// Inter-Rater Reliability (IRR) Utilities
// =========================================
// Client-side Cohen's Kappa computation and CSV export.

export interface KappaInput {
  decisions1: string[]; // decisions by reviewer 1
  decisions2: string[]; // decisions by reviewer 2
}

export interface KappaOutput {
  kappa: number;
  n: number;
  observedAgreement: number;
  expectedAgreement: number;
  interpretation: string;
}

export interface PercentAgreementOutput {
  agreement: number;
  n: number;
}

/**
 * Interpret a Kappa value using Landis & Koch (1977) scale.
 */
export function interpretKappa(kappa: number): string {
  if (kappa < 0) return 'Poor';
  if (kappa <= 0.20) return 'Slight';
  if (kappa <= 0.40) return 'Fair';
  if (kappa <= 0.60) return 'Moderate';
  if (kappa <= 0.80) return 'Substantial';
  return 'Almost Perfect';
}

/**
 * Compute Cohen's Kappa for two sets of decisions.
 * Both arrays must be the same length and in the same order (matched by source).
 */
export function computeCohensKappa(input: KappaInput): KappaOutput {
  const { decisions1, decisions2 } = input;
  const n = Math.min(decisions1.length, decisions2.length);

  if (n === 0) {
    return {
      kappa: 0,
      n: 0,
      observedAgreement: 0,
      expectedAgreement: 0,
      interpretation: 'No shared decisions',
    };
  }

  // Count agreements
  let agreements = 0;
  for (let i = 0; i < n; i++) {
    if (decisions1[i] === decisions2[i]) agreements++;
  }
  const observed = agreements / n;

  // Get all unique categories
  const categories = new Set([...decisions1.slice(0, n), ...decisions2.slice(0, n)]);

  // Expected agreement
  let expected = 0;
  for (const cat of categories) {
    const p1 = decisions1.slice(0, n).filter((d) => d === cat).length / n;
    const p2 = decisions2.slice(0, n).filter((d) => d === cat).length / n;
    expected += p1 * p2;
  }

  // Kappa
  const kappa = expected >= 1.0 ? 1.0 : (observed - expected) / (1.0 - expected);
  const rounded = Math.round(kappa * 10000) / 10000;

  return {
    kappa: rounded,
    n,
    observedAgreement: Math.round(observed * 10000) / 10000,
    expectedAgreement: Math.round(expected * 10000) / 10000,
    interpretation: interpretKappa(rounded),
  };
}

/**
 * Compute simple percent agreement between two sets of decisions.
 */
export function computePercentAgreement(input: KappaInput): PercentAgreementOutput {
  const { decisions1, decisions2 } = input;
  const n = Math.min(decisions1.length, decisions2.length);
  if (n === 0) return { agreement: 0, n: 0 };

  let agreements = 0;
  for (let i = 0; i < n; i++) {
    if (decisions1[i] === decisions2[i]) agreements++;
  }

  return {
    agreement: Math.round((agreements / n) * 10000) / 10000,
    n,
  };
}

/**
 * Export IRR data as CSV string.
 */
export function irrToCSV(
  pairs: Array<{
    reviewer1: string;
    reviewer2: string;
    n: number;
    agreement: number;
    kappa: number | null;
    interpretation: string;
  }>,
): string {
  const header = 'Reviewer A,Reviewer B,N,% Agreement,Kappa,Interpretation';
  const rows = pairs.map((p) =>
    [
      `"${p.reviewer1}"`,
      `"${p.reviewer2}"`,
      p.n,
      `${Math.round(p.agreement * 100)}%`,
      p.kappa != null ? p.kappa.toFixed(4) : 'N/A',
      p.interpretation,
    ].join(','),
  );
  return [header, ...rows].join('\n');
}
